/**
 * Report Detail View for Forensic Accountants
 * Shows comprehensive financial data and calculations
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAccountantAuth } from '../contexts/AccountantAuthContext';
import axios from '@/lib/http';
import AccountantLayout from '../components/AccountantLayout';
import CalculationMethodology from '../components/CalculationMethodology';
import VersionHistory from '../components/VersionHistory';
import ESignature from '../components/ESignature';
import ScenarioProjectionsAccountant from '../components/ScenarioProjectionsAccountant';
import ParsedPLDisplay from '../components/ParsedPLDisplay';
import CaseReadinessPanel from '../components/CaseReadinessPanel';
import AIActivityPanel from '../components/AIActivityPanel';
import ExtractedDocumentsPanel from '../components/ExtractedDocumentsPanel';
import { CompactPipelineStatus, useAgentUpdates } from '../components/pipeline';
import { AccountantKeyMetrics, BusinessInfoCard, PayrollReconciliationCard, SourceDocumentsSection, ComparativeAnalysisSection, SensitivityAnalysisSection, QAInsightsSection, BenchmarkPeriodCard, VariableProfitAnalysisCard, GrowthAdjustmentCard, FinalCalculationCard, ComputedSurfaces, CarrierCounterModelSection, CourtPDFExportSection, ForcedClosureDamagesSection } from '../components/accountant';
import { ReconstructedIndemnityCard, ReconstructedIndemnity } from '../components/accountant/ReconstructedIndemnityCard';
import { MethodologyNarrativeCard, MethodologySummary } from '../components/accountant/MethodologyNarrativeCard';
import { ArrowLeft, FileText, AlertTriangle, FileSpreadsheet, FileCheck, Search, Clock } from 'lucide-react';
import DraftWatermark from '../components/DraftWatermark';
import { FinancialValue } from '@/components/shared/FinancialValue';
import { formatCurrency } from '@/utils/financialFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface Report {
  id: string;
  client_name: string;
  claimant_name: string | null;
  business_address: string | null;
  business_type: string;
  zone: string;
  event_type?: string;
  /** 'standard_bi' | 'future_closure' — gates claim-type-specific tools like
   *  the Carrier Counter-Model (VP reclassification applies to standard-BI only). */
  calculation_mode?: string;
  event_start_date?: string;
  event_end_date?: string;
  coverage_year?: number;
  year_window?: number[];
  status: string;
  created_at: string;
  completed_at?: string;
  // Engagement-detail surfaces (WS2) — top-level on the report doc, provenance-tagged.
  reconstructed_indemnity?: ReconstructedIndemnity | null;
  methodology_summary?: MethodologySummary | null;
  /** Canonical binding net (engine policy_deductions); preferred over result.final_claim_amount. */
  net_supported_claim?: number | null;
  figures_run_id?: string | null;
  /** 'report' | 'report_via_draft' | 'draft' — how /view found this doc. */
  identity_source?: string | null;
  legacy_final_claim_amount?: number | null;
  result?: {
    final_claim_amount: number;
    revenue_loss: number;
    causation_passed: boolean;
    step1_variable_profit_loss?: number;
    step2_incremental_loss?: number;
    causation_test_used?: string;
    calculation_detail?: any;
    payroll_reconciliation?: any;
    // Engine-truth per-year revenue + variable profit (from validate-report ->
    // calculate_claim). Frontend RENDERS this; it never re-derives VP from a margin.
    yearly_breakdown?: Array<{ year: number; revenue: number; variable_profit: number }>;
  };
  // Real engine sensitivity sweep (populated by POST /api/v1/reports/{id}/sensitivity).
  // Shape: { base_claim, parameter, results: [{pct, claim, change_pct}], range: {min, max, spread} }
  sensitivity_analysis?: {
    base_claim?: number;
    parameter?: string;
    results?: Array<{ pct: number; claim: number; change_pct?: number }>;
    range?: { min: number; max: number; spread: number };
    run_at?: string;
  } | null;
  financial_data?: any;
  // P&L Bypass fields (Phase 1.5)
  has_pl_statement?: boolean;
  parsed_pl_statement?: any;
  pl_parsed_at?: string;
  // TS-FU3 archaeology (2026-04-29): fields the component reads but the
  // local Report interface had drifted away from. Per the audit's "lost
  // feature" hypothesis, these were on the type before some refactor and
  // got dropped while the read sites stayed. Restoring as `?: any` /
  // narrow optionals to clear the TS2339 cluster without re-deriving the
  // full historical schema.
  narrative?: any;
  narrative_generated_at?: string;
  tax1065Files?: any[];
  plFiles?: any[];
  uploadedDocuments?: any[];
}

// Helper component to show pipeline status with hook
function PipelineStatusLine({ claimId }: { claimId: string }) {
  const { pipeline, loading } = useAgentUpdates(claimId);
  return (
    <div className="mt-2">
      <CompactPipelineStatus pipeline={pipeline} loading={loading} />
    </div>
  );
}

export default function AccountantReportView() {
  const { reportId } = useParams<{ reportId: string }>();
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [permissions, setPermissions] = useState<string[]>([]);
  const [comparativeAnalysis, setComparativeAnalysis] = useState<any>(null);
  const [reviewInsights, setReviewInsights] = useState<any>(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportMessage, setReportMessage] = useState<string | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [profileStatus, setProfileStatus] = useState<any>(null);
  const [justRegenerated, setJustRegenerated] = useState(false);
  const [previousReportTimestamp, setPreviousReportTimestamp] = useState<string | null>(null);
  const [pendingInvitesCount, setPendingInvitesCount] = useState(0);
  // Engine-outputs source for the policy-deduction waterfall + computed surfaces.
  // Assembled fail-soft from /engine-review/{id}/{engine}/output (+ carrier-advances);
  // the /view report has no policy_deductions. Each surface hides on a miss.
  const [computedSource, setComputedSource] = useState<any>(null);
  
  const { accountant, loading: authLoading, getAuthHeader } = useAccountantAuth();
  const navigate = useNavigate();

  // In test mode, bypass accountant auth requirement
  const isTestMode = process.env.REACT_APP_DISABLE_AUTH === 'true';

  // Memoize the test accountant to prevent re-render loops
  const testAccountant = React.useMemo(() => ({
    id: 'test_user_123',
    email: 'test@claimsync.com',
    full_name: 'Test Accountant'
  }), []);

  const effectiveAccountant = isTestMode ? testAccountant : accountant;

  useEffect(() => {
    if (authLoading) return;
    if (!effectiveAccountant) {
      navigate('/accountant/login');
      return;
    }

    fetchReport();
    fetchPendingInvitesCount();
  }, [reportId, effectiveAccountant, authLoading]);

  const fetchPendingInvitesCount = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/collaboration/invites/pending`,
        { headers: getAuthHeader() }
      );
      setPendingInvitesCount(response.data.length);
    } catch (err: any) {
      console.error('Failed to fetch pending invites count:', err);
    }
  };

  const fetchReport = async () => {
    try {
      // Clear the prior report's computed surfaces before a reload so an in-place
      // reportId change can't transiently show report A's waterfall/N under B.
      setComputedSource(null);
      // Get the report data using the accountant-specific endpoint
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/view`,
        { headers: getAuthHeader() }
      );
      
      setReport(response.data);
      
      // Get permissions for this report
      const sharesResponse = await axios.get(
        `${BACKEND_URL}/api/v1/collaboration/reports/shared`,
        { headers: getAuthHeader() }
      );
      
      const sharedReport = sharesResponse.data.find((r: any) => r.report_id === reportId);
      if (sharedReport) {
        setPermissions(sharedReport.permissions);
      }

      // Fetch AI-powered comparative analysis
      fetchComparativeAnalysis();
      
      // Fetch AI-powered review insights
      fetchReviewInsights();
      
      // Check accountant profile status
      checkProfileStatus();

      // Assemble the engine-outputs source for the policy-deduction waterfall + surfaces.
      fetchComputedSurfaces();

    } catch (err: any) {
      console.error('Failed to fetch report:', err);
      setError(err.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  // Read-only: assemble the S1 engine-outputs map the computed-surfaces picker expects.
  // Fail-soft — each fetch is independent; a miss just hides that surface. No dollars are
  // computed here; the picker reads already-computed engine values by their verified keys.
  const fetchComputedSurfaces = async () => {
    const grab = async (path: string): Promise<any | null> => {
      try {
        const r = await axios.get(`${BACKEND_URL}${path}`, { headers: getAuthHeader() });
        return r.data;
      } catch {
        return null;
      }
    };
    try {
      const [pd, mit, cu, adv] = await Promise.all([
        grab(`/api/v1/engine-review/${reportId}/policy_deductions/output`),
        grab(`/api/v1/engine-review/${reportId}/mitigation/output`),
        grab(`/api/v1/engine-review/${reportId}/catch_up_sales/output`),
        grab(`/api/v1/adjustments/carrier-advances/${reportId}`),
      ]);
      const src: Record<string, unknown> = {};
      if (pd?.outputs) src.policy_deductions = { ...pd.outputs, stale: Boolean(pd.stale) };
      if (mit?.outputs) src.mitigation = mit.outputs;
      if (cu?.outputs) src.catch_up_sales = cu.outputs;
      if (adv) src.carrier_advances = adv; // { advances, total_cents }
      setComputedSource(Object.keys(src).length ? src : null);
    } catch (err) {
      console.error('Failed to assemble computed surfaces:', err);
    }
  };

  const fetchComparativeAnalysis = async () => {
    setLoadingAnalysis(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/comparative-analysis`,
        { headers: getAuthHeader() }
      );
      setComparativeAnalysis(response.data);
    } catch (err) {
      console.error('Failed to fetch comparative analysis:', err);
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const fetchReviewInsights = async () => {
    setLoadingInsights(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/review-insights`,
        { headers: getAuthHeader() }
      );
      setReviewInsights(response.data);
    } catch (err) {
      console.error('Failed to fetch review insights:', err);
    } finally {
      setLoadingInsights(false);
    }
  };


  const checkProfileStatus = async () => {
    try {
      console.log('Checking profile status...');
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/accountant-profile/check`,
        { headers: getAuthHeader() }
      );
      console.log('Profile status response:', response.data);
      setProfileStatus(response.data);
    } catch (err) {
      console.error('Failed to check profile status:', err);
      // Set default status on error
      setProfileStatus({ has_profile: false, profile_completed: false });
    }
  };

  const handleGenerateExpertReport = async () => {
    // Check if profile is completed
    if (!profileStatus?.profile_completed) {
      if (window.confirm('You need to complete your profile first to generate court-ready expert reports. Complete profile now?')) {
        navigate('/accountant/profile');
      }
      return;
    }

    // Explicit review confirmation
    const confirmed = window.confirm(
      `PROFESSIONAL ATTESTATION CONFIRMATION\n\n` +
      `By clicking OK, you certify that:\n\n` +
      `✓ You have thoroughly reviewed all financial data and source documents\n` +
      `✓ You have verified the BI methodology application and calculations\n` +
      `✓ You have examined the causation analysis and statistical validation\n` +
      `✓ The findings and conclusions are accurate to the best of your professional knowledge\n\n` +
      `This will generate a court-ready expert report with YOUR credentials and professional attestation.\n\n` +
      `Client: ${report.client_name}\n` +
      `Claim Amount: ${formatCurrency(headline, { cents: true })}\n\n` +
      `Proceed with expert report generation?`
    );
    
    if (!confirmed) {
      return;
    }

    // Save current report timestamp if it exists
    if (report?.narrative_generated_at) {
      setPreviousReportTimestamp(report.narrative_generated_at);
    }
    
    setGeneratingReport(true);
    setJustRegenerated(false);
    
    // Show progress updates
    const startTime = Date.now();
    const progressInterval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      console.log(`⏱️ Report generation in progress: ${elapsed}s elapsed...`);
    }, 5000);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/narrator/generate-narrative-accountant/${reportId}`,
        {},
        { 
          headers: getAuthHeader(),
          timeout: 300000  // 5 minute timeout
        }
      );

      clearInterval(progressInterval);
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      
      // Mark as just regenerated
      setJustRegenerated(true);
      
      // Show success notification
      setReportMessage(`Expert report generated successfully! Generated in ${elapsed} seconds. ${response.data.estimated_pages} pages, ${response.data.word_count} words.`);
      
      // Refresh the report to show the narrative
      await fetchReport();
      
      // Scroll to the report section
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Auto-clear the "just regenerated" banner after 10 seconds
      setTimeout(() => {
        setJustRegenerated(false);
      }, 10000);
      
    } catch (error: any) {
      clearInterval(progressInterval);
      console.error('Failed to generate expert report:', error);
      
      let errorMsg = 'Failed to generate expert report';
      if (error.code === 'ECONNABORTED') {
        errorMsg = 'Report generation timed out. Please try again or contact support if the issue persists.';
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      
      setReportError(errorMsg);
    } finally {
      setGeneratingReport(false);
    }
  };

  const formatPercent = (value?: number) => {
    if (!value && value !== 0) return 'N/A';
    return `${value.toFixed(2)}%`;
  };

  if (loading) {
    return (
      <AccountantLayout>
        <div className="flex items-center justify-center min-h-[60vh]" style={{ backgroundColor: 'var(--color-bg-base)' }}>
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 mb-4" style={{ borderBottom: '2px solid var(--color-brand)' }}></div>
            <p className="cs-body">Loading report...</p>
          </div>
        </div>
      </AccountantLayout>
    );
  }

  if (error || !report) {
    return (
      <AccountantLayout>
        <div className="flex items-center justify-center min-h-[60vh]" style={{ backgroundColor: 'var(--color-bg-base)' }}>
          <div className="text-center max-w-md">
            <AlertTriangle className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--color-brand)' }} />
            <h1 className="cs-h2 mb-2">Access Denied</h1>
            <p className="cs-body mb-6">{error || 'Report not found or you do not have access'}</p>
            <Link
              to="/accountant"
              className="cs-body inline-flex items-center gap-2"
              style={{ padding: '12px 24px', borderRadius: 10, backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 500 }}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Link>
          </div>
        </div>
      </AccountantLayout>
    );
  }

  const result = report.result;
  const headline = report.net_supported_claim ?? result?.final_claim_amount;
  const hasCanonicalNet = report.net_supported_claim != null;
  const calcDetail = result?.calculation_detail;

  return (
    <AccountantLayout>
      <div className="relative p-6" style={{ backgroundColor: 'var(--color-bg-base)' }}>
        <DraftWatermark status={report.status} />
        {/* Page Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="cs-display">{report.client_name}</h1>
            <p className="cs-meta">{[report.claimant_name, report.business_address].filter(Boolean).join(' • ')}</p>
            {/* AI Pipeline Status */}
            <PipelineStatusLine claimId={reportId!} />
          </div>

          <div className="flex items-center gap-3">
            <span
              className="cs-chip"
              style={
                report.status === 'completed'
                  ? { padding: '6px 12px', borderRadius: 9999, backgroundColor: 'transparent', color: 'var(--color-verified)' }
                  : report.status === 'in_progress'
                  ? { padding: '6px 12px', borderRadius: 9999, backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                  : { padding: '6px 12px', borderRadius: 9999, backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }
              }
            >
              {report.status}
            </span>
            {/* Engine Review CTA (Phase 1.1 redesign) */}
            <button
              onClick={() => navigate(`/accountant/review/${reportId}`)}
              className="cs-body flex items-center gap-2"
              style={{
                padding: '8px 16px',
                borderRadius: 10,
                fontWeight: 500,
                backgroundColor: 'var(--color-brand)',
                color: 'var(--color-on-brand)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-brand-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-brand)';
              }}
            >
              <FileCheck className="w-4 h-4" />
              Engine Review & Attest
            </button>
            <button
              onClick={() => navigate(`/accountant/reconciliation/${reportId}`)}
              className="cs-body flex items-center gap-2"
              style={{ padding: '8px 16px', borderRadius: 10, fontWeight: 500, backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
            >
              <FileSpreadsheet className="w-4 h-4" />
              P&L Reconciliation
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-6">
        
        {/* Key Metrics */}
        <AccountantKeyMetrics
          finalClaimAmount={headline}
          headlineLabel={hasCanonicalNet ? 'Net supported claim' : 'Final Claim Amount'}
          legacyAmount={hasCanonicalNet ? result?.final_claim_amount : undefined}
          variableProfitLoss={result?.step1_variable_profit_loss}
          incrementalLoss={result?.step2_incremental_loss}
          causationPassed={result?.causation_passed}
        />

        {/* AdjusterAnalysisSection, AttorneyAnalysisSection and FairSettlementSection
            are intentionally OMITTED on the external-accountant view. They POST to
            Clerk-only staff endpoints (/forensic/analyze, /attorney/analyze,
            /mediation/calculate) which reject the accountant JWT with 401. These
            surfaces are in-house work product, not part of the external witness
            accountant's attestation scope. */}

        {/* CARRIER COUNTER-MODEL — adversarial reclassification workbench.
            Sits between the carrier narrative lens and our attorney lens:
            carrier narrative → carrier math → our case.
            Everything inside is a HYPOTHETICAL overlay; it never mutates the
            filed calculation. */}
        {report && (report.calculation_mode ?? 'standard_bi') === 'standard_bi' && (
          <CarrierCounterModelSection
            reportId={reportId!}
            getAuthHeader={getAuthHeader}
            formatCurrency={formatCurrency}
            financialData={report.financial_data}
            coverageYear={report.coverage_year}
          />
        )}

        {/* COURT-READY PDF EXPORT */}
        {report && (
          <CourtPDFExportSection
            reportId={reportId!}
            claimantName={report.claimant_name || report.client_name}
            getAuthHeader={getAuthHeader}
          />
        )}

        {/* Business Information */}
        <BusinessInfoCard
          businessType={report.business_type}
          businessAddress={report.business_address}
          zone={report.zone}
          coverageYear={report.coverage_year}
          eventType={report.event_type}
          eventStartDate={report.event_start_date}
          eventEndDate={report.event_end_date}
        />

        {/* Reconstructed Indemnity (WS2) — engagement headline from real engine output */}
        <ReconstructedIndemnityCard
          reconstructedIndemnity={report.reconstructed_indemnity}
          filedClaimAmount={report.identity_source === 'report_via_draft' ? null : result?.final_claim_amount}
          filedClaimComputedAt={report.completed_at}
        />

        {/* Calculation Breakdown */}
        {calcDetail && (
          <>
            {/* Benchmark Period */}
            {calcDetail.benchmark_period && (
              <BenchmarkPeriodCard
                benchmarkPeriod={calcDetail.benchmark_period}
                formatCurrency={formatCurrency}
              />
            )}

            {/* Variable Profit Analysis */}
            {calcDetail.variable_profit_analysis && (
              <VariableProfitAnalysisCard
                variableProfitAnalysis={calcDetail.variable_profit_analysis}
                benchmarkPeriod={calcDetail.benchmark_period}
                formatCurrency={formatCurrency}
                formatPercent={formatPercent}
              />
            )}

            {/* Growth Adjustment */}
            {calcDetail.growth_adjustment && (
              <GrowthAdjustmentCard
                growthAdjustment={calcDetail.growth_adjustment}
                benchmarkRevenue={calcDetail.benchmark_period?.revenue}
                variableMarginPct={calcDetail.variable_profit_analysis?.variable_margin_pct}
                financialData={report.financial_data}
                formatCurrency={formatCurrency}
                formatPercent={formatPercent}
              />
            )}

            {/* Final Calculation */}
            {calcDetail.final_calculation && (
              <FinalCalculationCard
                finalCalculation={calcDetail.final_calculation}
                formatCurrency={formatCurrency}
                policyDeductions={computedSource?.policy_deductions}
              />
            )}

            {/* Policy-deduction control + offsets (read-only, engine outputs; self-hides if absent) */}
            <ComputedSurfaces source={computedSource} />
          </>
        )}

        {/* Methodology narrative (WS2) — authority/posture layer after the step math */}
        <MethodologyNarrativeCard methodologySummary={report.methodology_summary} />

        {/* Payroll Reconciliation */}
        {result?.payroll_reconciliation && (
          <PayrollReconciliationCard
            payrollReconciliation={result.payroll_reconciliation}
            reportId={reportId!}
            canComment={permissions.includes('comment')}
            getAuthHeader={getAuthHeader}
            formatCurrency={formatCurrency}
            formatPercent={formatPercent}
          />
        )}


        {/* COMPARATIVE ANALYSIS SECTION */}
        {result && (
          <ComparativeAnalysisSection
            reportId={reportId!}
            businessType={report.business_type}
            comparativeAnalysis={comparativeAnalysis}
            loadingAnalysis={loadingAnalysis}
            canComment={permissions.includes('comment')}
            getAuthHeader={getAuthHeader}
            formatCurrency={formatCurrency}
          />
        )}
        {/* FORCED-CLOSURE DAMAGES (phased pro-forma) — self-gates to FC cases */}
        {report?.id && (
          <ForcedClosureDamagesSection reportId={report.id} />
        )}
        {/* SENSITIVITY ANALYSIS SECTION */}
        {result && (
          <SensitivityAnalysisSection
            sensitivityAnalysis={report.sensitivity_analysis}
            reportId={report.id}
            formatCurrency={formatCurrency}
          />
        )}

        {/* INTERACTIVE LOSS PROJECTION SCENARIOS - Dynamic What-If Analysis */}
        {report && result && (
          <ScenarioProjectionsAccountant 
            reportId={report.id} 
            baseResult={result}
          />
        )}

        {/* AI ACTIVITY TRAIL */}
        <AIActivityPanel reportId={reportId!} className="mb-6" />

        {/* Q&A BOT INSIGHTS SECTION */}
        {result && (
          <QAInsightsSection
            result={result}
            report={report}
            calcDetail={calcDetail}
            formatCurrency={formatCurrency}
            formatPercent={formatPercent}
          />
        )}


        {/* SOURCE DOCUMENTS SECTION */}
        <SourceDocumentsSection
          reportId={reportId!}
          clientName={report.client_name}
          tax1065Files={report.tax1065Files}
          plFiles={report.plFiles}
          uploadedDocuments={report.uploadedDocuments}
          getAuthHeader={getAuthHeader}
        />

        {/* PARSED P&L STATEMENT SECTION (Phase 1.5) */}
        {report.has_pl_statement && report.parsed_pl_statement && (
          <div className="mb-6">
            <h2 className="cs-h2 mb-4 flex items-center">
              <FileSpreadsheet className="w-6 h-6 mr-2" style={{ color: 'var(--color-text-muted)' }} />
              Parsed P&L Statement
              <span className="cs-chip ml-2" style={{ padding: '2px 8px', borderRadius: 6, backgroundColor: 'transparent', color: 'var(--color-verified)' }}>
                Reconstruction Bypassed
              </span>
            </h2>
            <ParsedPLDisplay data={report.parsed_pl_statement} showDetails={true} />
          </div>
        )}

        {/* CASE READINESS & SUPPORTING DOCUMENTS (P0/P2) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Case Readiness Panel */}
          <div style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }}>
            <h2 className="cs-h2 mb-4">Case Readiness</h2>
            <CaseReadinessPanel caseId={reportId || ''} compact={false} showRefresh={true} />
          </div>

          {/* Extracted Supporting Documents */}
          <div style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }}>
            <ExtractedDocumentsPanel caseId={reportId || ''} />
          </div>
        </div>


        {/* MONTHLY DATA TABLES SECTION */}
        {(() => {
          // Transform data_YYYY format to yearly_data format for display
          const yearlyData: any = {};
          if (report.financial_data) {
            Object.keys(report.financial_data).forEach(key => {
              if (key.startsWith('data_')) {
                const year = key.replace('data_', '');
                yearlyData[year] = report.financial_data[key];
              }
            });
          }
          
          return Object.keys(yearlyData).length > 0 && (
            <div style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }} className="mb-6">
              <h2 className="cs-h2 mb-4 flex items-center">
                Monthly Financial Data - Raw Numbers
              </h2>
              <p className="cs-body mb-6">
                Complete monthly breakdown of revenue, expenses, and payroll. This is the source data used in all calculations above.
              </p>

              {/* Revenue Table */}
              <div className="mb-8 overflow-x-auto">
                <h3 className="cs-h3 mb-3">Monthly Revenue Breakdown</h3>
                <table className="min-w-full">
                  <thead>
                    <tr style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                      <th className="cs-label px-3 py-2 text-left">Year</th>
                      {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(month => (
                        <th key={month} className="cs-label px-3 py-2 text-right">{month}</th>
                      ))}
                      <th className="cs-label px-3 py-2 text-right" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(yearlyData)
                      .sort(([yearA], [yearB]) => parseInt(yearA) - parseInt(yearB))
                      .map(([year, months]: [string, any]) => {
                        const yearTotal = months.reduce((sum: number, m: any) => sum + (m.revenue || 0), 0);
                        const isCoverageYear = parseInt(year) === report.coverage_year;
                        const isBenchmarkYear = calcDetail?.benchmark_period?.start?.includes(year);

                        return (
                          <tr key={year} style={{ borderBottom: '1px solid var(--color-border-faint)', backgroundColor: isCoverageYear ? 'var(--color-needs-human-tint)' : isBenchmarkYear ? 'transparent' : 'transparent' }}>
                            <td className="cs-body px-3 py-2" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
                              {year}
                              {isCoverageYear && <span className="ml-2 cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>◆ Coverage</span>}
                              {isBenchmarkYear && <span className="ml-2 cs-meta" style={{ color: 'var(--color-verified)' }}>* Benchmark</span>}
                            </td>
                            {months.map((monthData: any, idx: number) => (
                              <td key={idx} className="cs-figure px-3 py-2 text-right">
                                {formatCurrency(monthData.revenue).replace('$', '').replace(',000', 'K')}
                              </td>
                            ))}
                            <td className="cs-figure px-3 py-2 text-right" style={{ fontWeight: 700, color: 'var(--color-text-primary)', backgroundColor: 'var(--color-bg-elevated)' }}>
                              <FinancialValue value={yearTotal} />
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                <div className="mt-2 flex gap-4">
                  <span className="cs-meta" style={{ color: 'var(--color-verified)' }}>* Benchmark Period months used in calculation</span>
                  <span className="cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>◆ Coverage Period (interruption)</span>
                </div>
              </div>

              {/* Expense Summary Table */}
              <div className="overflow-x-auto">
                <h3 className="cs-h3 mb-3">Annual Expense Summary</h3>
                <table className="min-w-full">
                  <thead>
                    <tr style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                      <th className="cs-label px-3 py-2 text-left">Year</th>
                      <th className="cs-label px-3 py-2 text-right">Total Revenue</th>
                      <th className="cs-label px-3 py-2 text-right">Variable Expenses</th>
                      <th className="cs-label px-3 py-2 text-right">Fixed Expenses</th>
                      <th className="cs-label px-3 py-2 text-right">Total Payroll</th>
                      <th className="cs-label px-3 py-2 text-right" style={{ backgroundColor: 'transparent' }}>Variable Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(yearlyData)
                      .sort(([yearA], [yearB]) => parseInt(yearA) - parseInt(yearB))
                      .map(([year, months]: [string, any]) => {
                        const yearRevenue = months.reduce((sum: number, m: any) => sum + (m.revenue || 0), 0);
                        // Calculate payroll from either payroll.total or total_payroll
                        const yearPayroll = months.reduce((sum: number, m: any) => {
                          if (m.payroll && typeof m.payroll === 'object') {
                            return sum + (m.payroll.total || 0);
                          }
                          return sum + (m.total_payroll || 0);
                        }, 0);
                        // Calculate officer compensation (fixed salary component)
                        const yearOfficerComp = months.reduce((sum: number, m: any) => {
                          if (m.payroll && typeof m.payroll === 'object') {
                            return sum + (m.payroll.officer_compensation || 0);
                          }
                          return sum + (m.officer_compensation || 0);
                        }, 0);
                        // Calculate fixed expenses from expense breakdown (Rent, Utilities, etc.)
                        const yearFixedExpenses = months.reduce((sum: number, m: any) => {
                          if (m.expenses && typeof m.expenses === 'object') {
                            const rent = m.expenses['Rent'] || m.expenses['rent'] || 0;
                            const utilities = m.expenses['Utilities'] || m.expenses['utilities'] || 0;
                            const insurance = m.expenses['Insurance'] || m.expenses['insurance'] || 0;
                            return sum + rent + utilities + insurance;
                          }
                          return sum;
                        }, 0);
                        // Variable Profit is computed by the deterministic engine
                        // (calculate_all_variable_profits) and surfaced per-year via
                        // result.yearly_breakdown ([{year, revenue, variable_profit}]).
                        // We RENDER that engine value — never re-derive VP from a margin.
                        // If the engine breakdown is absent for this year, show "—".
                        const engineYear = (result as any)?.yearly_breakdown?.find(
                          (e: any) => String(e.year) === String(year)
                        );
                        const hasEngineVP = engineYear && typeof engineYear.variable_profit === 'number';
                        const variableProfit = hasEngineVP ? engineYear.variable_profit : null;
                        // Variable expenses = revenue − VP, but only when VP is engine-provided.
                        const variableExpenses = hasEngineVP ? (yearRevenue - engineYear.variable_profit) : null;

                        return (
                          <tr key={year} style={{ borderBottom: '1px solid var(--color-border-faint)' }}>
                            <td className="cs-body px-3 py-2" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{year}</td>
                            <td className="cs-figure px-3 py-2 text-right"><FinancialValue value={yearRevenue} /></td>
                            <td className="cs-figure px-3 py-2 text-right">{variableExpenses !== null ? <FinancialValue value={variableExpenses} /> : '—'}</td>
                            <td className="cs-figure px-3 py-2 text-right">
                              {yearFixedExpenses > 0 ? <FinancialValue value={yearFixedExpenses} /> : yearOfficerComp > 0 ? <FinancialValue value={yearOfficerComp} /> : '—'}
                            </td>
                            <td className="cs-figure px-3 py-2 text-right"><FinancialValue value={yearPayroll} /></td>
                            <td className="cs-figure px-3 py-2 text-right" style={{ fontWeight: 700, color: variableProfit !== null ? 'var(--color-verified)' : 'var(--color-text-muted)', backgroundColor: 'transparent' }}>{variableProfit !== null ? <FinancialValue value={variableProfit} /> : '—'}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                <div className="mt-2">
                  <span className="cs-meta" style={{ color: 'var(--color-text-muted)' }}>
                    Variable Profit is computed by the deterministic engine (per-year breakdown). A "—" indicates the engine value is not available for that year — recalculate to populate it. Values are never approximated from a margin.
                  </span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* DATA TRAIL SECTION */}
        {result && calcDetail && (
          <div style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }} className="mb-6">
            <h2 className="cs-h2 mb-4 flex items-center">
              <Search className="w-5 h-5 mr-2" aria-hidden="true" />
              Data Trail - Source to Result
            </h2>
            <p className="cs-body mb-6">
              Visual flowchart showing how source documents became final claim amount. Critical for court testimony and audit verification.
            </p>

            <div className="space-y-4">
              {/* Step 1 */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 cs-figure" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)', fontWeight: 700 }}>1</div>
                <div className="flex-1" style={{ backgroundColor: 'var(--color-bg-elevated)', borderLeft: '4px solid var(--color-border)', borderRadius: 10, padding: 16 }}>
                  <p className="cs-body mb-2" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>SOURCE: Financial Documents</p>
                  <p className="cs-body">
                    {report.plFiles?.length || 0} P&L statements, {report.tax1065Files?.length || 0} tax returns uploaded and processed
                  </p>
                </div>
              </div>

              <div className="ml-6" style={{ color: 'var(--color-text-muted)' }}>↓</div>

              {/* Step 2 */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 cs-figure" style={{ backgroundColor: 'transparent', color: 'var(--color-verified)', fontWeight: 700 }}>2</div>
                <div className="flex-1" style={{ backgroundColor: 'transparent', borderLeft: '4px solid var(--color-verified)', borderRadius: 10, padding: 16 }}>
                  <p className="cs-body mb-2" style={{ fontWeight: 600, color: 'var(--color-verified)' }}>EXTRACTED: Monthly Data</p>
                  <p className="cs-body">
                    {(() => {
                      let yearCount = 0;
                      if (report.financial_data) {
                        yearCount = Object.keys(report.financial_data).filter(k => k.startsWith('data_')).length;
                        if (report.financial_data.yearly_data) {
                          yearCount = Math.max(yearCount, Object.keys(report.financial_data.yearly_data).length);
                        }
                      }
                      return yearCount;
                    })()} years of monthly revenue/expense data extracted via OCR
                  </p>
                </div>
              </div>

              <div className="ml-6" style={{ color: 'var(--color-text-muted)' }}>↓</div>

              {/* Step 3 */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 cs-figure" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)', fontWeight: 700 }}>3</div>
                <div className="flex-1" style={{ backgroundColor: 'var(--color-bg-elevated)', borderLeft: '4px solid var(--color-border)', borderRadius: 10, padding: 16 }}>
                  <p className="cs-body mb-2" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>CALCULATED: Benchmark Period</p>
                  <p className="cs-figure">
                    {calcDetail.benchmark_period?.start} to {calcDetail.benchmark_period?.end}: Revenue = <FinancialValue value={calcDetail.benchmark_period?.revenue} />
                  </p>
                </div>
              </div>

              <div className="ml-6" style={{ color: 'var(--color-text-muted)' }}>↓</div>

              {/* Step 4 */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 cs-figure" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', fontWeight: 700 }}>4</div>
                <div className="flex-1" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderLeft: '4px solid var(--color-brand)', borderRadius: 10, padding: 16 }}>
                  <p className="cs-body mb-2" style={{ fontWeight: 600, color: 'var(--color-needs-human-text)' }}>APPLIED: Variable Margin</p>
                  <p className="cs-figure" style={{ color: 'var(--color-needs-human-text)' }}>
                    <FinancialValue value={calcDetail.benchmark_period?.revenue} /> × {formatPercent(calcDetail.variable_profit_analysis?.variable_margin_pct)} = <FinancialValue value={calcDetail.variable_profit_analysis?.benchmark_vp} />
                  </p>
                </div>
              </div>

              <div className="ml-6" style={{ color: 'var(--color-text-muted)' }}>↓</div>

              {/* Step 5 */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 cs-figure" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)', fontWeight: 700 }}>5</div>
                <div className="flex-1" style={{ backgroundColor: 'var(--color-bg-elevated)', borderLeft: '4px solid var(--color-border)', borderRadius: 10, padding: 16 }}>
                  <p className="cs-body mb-2" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>CALCULATED: Loss</p>
                  <p className="cs-figure">
                    Benchmark VP - Coverage VP = <FinancialValue value={calcDetail.variable_profit_analysis?.benchmark_vp} /> - <FinancialValue value={calcDetail.variable_profit_analysis?.coverage_vp} /> = <FinancialValue value={calcDetail.variable_profit_analysis?.vp_loss} />
                  </p>
                </div>
              </div>

              <div className="ml-6" style={{ color: 'var(--color-text-muted)' }}>↓</div>

              {/* Step 6 */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 cs-figure" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)', fontWeight: 700 }}>6</div>
                <div className="flex-1" style={{ backgroundColor: 'var(--color-bg-elevated)', borderLeft: '4px solid var(--color-border)', borderRadius: 10, padding: 16 }}>
                  <p className="cs-body mb-2" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>ADJUSTED: Growth Trend</p>
                  <p className="cs-figure">
                    <FinancialValue value={calcDetail.variable_profit_analysis?.vp_loss} /> + <FinancialValue value={calcDetail.growth_adjustment?.incremental_lost_profit} /> = <FinancialValue value={calcDetail.final_calculation?.total_before_threshold} />
                  </p>
                </div>
              </div>

              <div className="ml-6" style={{ color: 'var(--color-text-muted)' }}>↓</div>

              {/* Final Result */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 cs-figure" style={{ backgroundColor: 'var(--color-verified)', color: 'var(--color-on-brand)', fontWeight: 700 }}>✓</div>
                <div className="flex-1" style={{ backgroundColor: 'transparent', border: '1px solid var(--color-verified)', borderRadius: 10, padding: 24 }}>
                  <p className="cs-body mb-2" style={{ fontWeight: 700, fontSize: '1.25rem', color: 'var(--color-verified)' }}>{hasCanonicalNet ? 'NET SUPPORTED CLAIM' : 'FINAL CLAIM AMOUNT'}</p>
                  <p className="cs-figure-lg" style={{ fontSize: '2.25rem', color: 'var(--color-verified)' }}><FinancialValue value={headline} cents /></p>
                  <p className="cs-meta mt-2">Ready for litigation/settlement</p>
                </div>
              </div>
            </div>

            <div className="mt-6" style={{ backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, padding: 16 }}>
              <p className="cs-body">
                <span style={{ fontWeight: 600 }}>For Court Testimony:</span> "I reviewed {report.plFiles?.length || 0} P&L statements and {report.tax1065Files?.length || 0} tax returns,
                extracted {(() => {
                  let yearCount = 0;
                  if (report.financial_data) {
                    yearCount = Object.keys(report.financial_data).filter(k => k.startsWith('data_')).length;
                    if (report.financial_data.yearly_data) {
                      yearCount = Math.max(yearCount, Object.keys(report.financial_data.yearly_data).length);
                    }
                  }
                  return yearCount;
                })()} years of monthly data, applied BI methodology, 
                and calculated {hasCanonicalNet ? 'a net supported claim' : 'a final claim amount'} of <FinancialValue value={headline} cents />."
              </p>
            </div>
          </div>
        )}

        {/* AUDIT TRAIL NOTES SECTION */}
        {/* Report Metadata */}
        <div style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }}>
          <h2 className="cs-h2 mb-4 flex items-center">
            <span className="text-2xl mr-2">ℹ️</span>
            Report Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="cs-label">Report ID</p>
              <p className="cs-figure">{report.id}</p>
            </div>
            <div>
              <p className="cs-label">Status</p>
              <p className="cs-body capitalize" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{report.status}</p>
            </div>
            <div>
              <p className="cs-label">Created</p>
              <p className="cs-body" style={{ color: 'var(--color-text-primary)' }}>{new Date(report.created_at).toLocaleString()}</p>
            </div>
            {report.completed_at && (
              <div>
                <p className="cs-label">Completed</p>
                <p className="cs-body" style={{ color: 'var(--color-text-primary)' }}>{new Date(report.completed_at).toLocaleString()}</p>
              </div>
            )}
            {result?.causation_test_used && (
              <div>
                <p className="cs-label">Causation Test</p>
                <p className="cs-body" style={{ color: 'var(--color-text-primary)' }}>{result.causation_test_used}</p>
              </div>
            )}
          </div>
        </div>

        {/* EXPERT REPORT GENERATION - Bottom of Page After Review */}
        <div style={{ backgroundColor: 'transparent', border: '1px solid var(--color-verified)', borderRadius: 14, padding: 32 }} className="mt-8">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <svg className="w-12 h-12" style={{ color: 'var(--color-verified)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              {/* Show existing narrative if it exists */}
              {report.narrative && report.narrative.word_count > 0 ? (
                <div className="relative">
                  {/* NEW REPORT BANNER */}
                  {justRegenerated && (
                    <div className="mb-4 animate-pulse" style={{ backgroundColor: 'var(--color-verified)', color: 'var(--color-on-brand)', borderRadius: 10, padding: 16 }}>
                      <div className="flex items-center gap-3">
                        <svg className="w-8 h-8 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1">
                          <p className="cs-body" style={{ fontWeight: 700, fontSize: '1.125rem', color: 'var(--color-on-brand)' }}>NEW REPORT GENERATED!</p>
                          <p className="cs-meta" style={{ color: 'var(--color-on-brand)' }}>
                            Generated just now • This is the latest version with your credentials
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <h2 className="cs-h2 mb-2 flex items-center gap-2" style={{ color: 'var(--color-verified)' }}>
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Court-Ready Expert Report {justRegenerated ? '(NEW)' : 'Generated'}
                  </h2>
                  <div className="mb-4" style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-verified)', borderRadius: 10, padding: 16 }}>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <p className="cs-label">Pages</p>
                        <p className="cs-figure-lg" style={{ color: 'var(--color-verified)' }}>{report.narrative.estimated_pages}</p>
                      </div>
                      <div>
                        <p className="cs-label">Words</p>
                        <p className="cs-figure-lg" style={{ color: 'var(--color-verified)' }}>{report.narrative.word_count?.toLocaleString()}</p>
                      </div>
                    </div>
                    {report.narrative.generated_by_accountant && (
                      <div className="pt-3" style={{ borderTop: '1px solid var(--color-border-sub)' }}>
                        <p className="cs-body">
                          <span style={{ fontWeight: 600 }}>Generated by:</span> {report.narrative.generated_by_accountant}
                        </p>
                        {report.narrative.accountant_credentials && (
                          <p className="cs-meta">
                            <span style={{ fontWeight: 600 }}>Credentials:</span> {report.narrative.accountant_credentials}
                          </p>
                        )}
                        <div className="mt-2 p-2" style={justRegenerated ? { borderRadius: 8, backgroundColor: 'transparent', border: '1px solid var(--color-verified)' } : { borderRadius: 8, backgroundColor: 'var(--color-bg-elevated)' }}>
                          <p className="cs-body" style={justRegenerated ? { color: 'var(--color-verified)', fontWeight: 600 } : undefined}>
                            <svg className="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            Generated: {new Date(report.narrative_generated_at || '').toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                            {justRegenerated && <span className="ml-2 cs-chip" style={{ backgroundColor: 'var(--color-verified)', color: 'var(--color-on-brand)', padding: '2px 8px', borderRadius: 6 }}>LATEST</span>}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => window.open(`/accountant/narrator/${reportId}`, '_blank')}
                      disabled={generatingReport}
                      className={`flex-1 cs-body flex items-center justify-center gap-2 ${justRegenerated && !generatingReport ? 'animate-pulse' : ''}`}
                      style={
                        generatingReport
                          ? { padding: '12px 24px', borderRadius: 10, fontWeight: 700, backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)', opacity: 0.5, cursor: 'not-allowed' }
                          : justRegenerated
                          ? { padding: '12px 24px', borderRadius: 10, fontWeight: 700, backgroundColor: 'var(--color-verified)', color: 'var(--color-on-brand)' }
                          : { padding: '12px 24px', borderRadius: 10, fontWeight: 700, backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }
                      }
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      {generatingReport ? (
                        <span className="flex items-center justify-center gap-1">
                          <Clock className="w-4 h-4" aria-hidden="true" /> Old Report (Regenerating...)
                        </span>
                      ) : justRegenerated ? 'View NEW Report' : 'View Full Report'}
                    </button>

                    <button
                      onClick={() => {
                        if (window.confirm('Regenerate the expert report? This will replace the existing report.')) {
                          handleGenerateExpertReport();
                        }
                      }}
                      disabled={generatingReport}
                      className="cs-body flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ padding: '12px 24px', borderRadius: 10, fontWeight: 500, backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Regenerate
                    </button>
                  </div>

                  <p className="cs-meta mt-3 text-center">
                    Report is permanently saved - you can access it anytime without regenerating
                  </p>
                </div>
              ) : (
                <div>
                  <h2 className="cs-h2 mb-2">
                    Generate Court-Ready Expert Report
                  </h2>
              <p className="cs-body mb-4">
                After thoroughly reviewing all calculations, data, and methodology above, you can generate a comprehensive
                expert report with your professional attestation. This report will include your credentials and signature,
                making it ready for court proceedings.
              </p>

              <div className="mb-4" style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-verified)', borderRadius: 10, padding: 16 }}>
                <h3 className="cs-h3 mb-2">✓ Review Checklist:</h3>
                <ul className="cs-body space-y-1">
                  <li>• Verified all source documents and financial data</li>
                  <li>• Reviewed BI methodology application and calculations</li>
                  <li>• Confirmed causation analysis and statistical validation</li>
                  <li>• Examined comparative analysis and industry benchmarks</li>
                  <li>• Assessed assumptions and limitations</li>
                  <li>• Ready to provide professional attestation</li>
                </ul>
              </div>

              {/* Profile Status Indicator */}
              <div
                className="mb-4"
                style={
                  profileStatus?.profile_completed
                    ? { backgroundColor: 'transparent', border: '1px solid var(--color-verified)', borderRadius: 10, padding: 16 }
                    : { backgroundColor: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', borderRadius: 10, padding: 16 }
                }
              >
                {profileStatus?.profile_completed ? (
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5" style={{ color: 'var(--color-verified)' }} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div className="flex-1">
                      <p className="cs-body" style={{ fontWeight: 500, color: 'var(--color-verified)' }}>
                        ✓ Profile Complete - Ready to Generate Expert Reports
                      </p>
                      <p className="cs-meta mt-1" style={{ color: 'var(--color-verified)' }}>
                        Your credentials will be included in the report attestation.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="cs-body flex items-center gap-1" style={{ fontWeight: 500, color: 'var(--color-needs-human-text)' }}>
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" aria-hidden="true" /> Complete your profile first to generate expert reports with your credentials.
                    </p>
                    <button
                      onClick={() => navigate('/accountant/profile')}
                      className="mt-2 cs-body underline"
                      style={{ color: 'var(--color-needs-human-text)' }}
                    >
                      Complete Profile Now →
                    </button>
                  </div>
                )}
              </div>

              <button
                onClick={handleGenerateExpertReport}
                disabled={generatingReport || !profileStatus?.profile_completed}
                className="w-full cs-body flex items-center justify-center gap-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ padding: '16px 24px', borderRadius: 10, fontWeight: 700, fontSize: '1.125rem', backgroundColor: 'var(--color-verified)', color: 'var(--color-on-brand)' }}
              >
                {generatingReport ? (
                  <>
                    <svg className="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating Court-Ready Expert Report...
                  </>
                ) : (
                  <>
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    I Have Reviewed This Case - Generate Expert Report
                  </>
                )}
              </button>

              {profileStatus?.profile_completed && (
                <p className="cs-meta mt-3 text-center">
                  Report will be generated with credentials: <span style={{ fontWeight: 600 }}>{accountant?.email}</span>
                </p>
              )}

              {/* Loading Modal Overlay */}
              {generatingReport && (
                <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
                  <div className="max-w-md w-full mx-4 text-center" style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 32 }}>
                    <div className="mb-6">
                      <svg className="animate-spin h-16 w-16 mx-auto" style={{ color: 'var(--color-verified)' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>

                    <h3 className="cs-h2 mb-3">
                      Generating Expert Report
                    </h3>

                    <p className="cs-body mb-4">
                      AI is analyzing the case and generating a professional report with your credentials...
                    </p>

                    <div className="mb-4" style={{ backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, padding: 16 }}>
                      <p className="cs-body">
                        <strong>⏱️ Estimated Time:</strong> 2-3 minutes
                      </p>
                      <p className="cs-meta mt-2">
                        Generating 5 sections with simplified, professional language
                      </p>
                    </div>

                    <div className="space-y-2 text-left cs-body">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: 'var(--color-verified)' }}></div>
                        <span>Analyzing financial data...</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: 'var(--color-verified)', animationDelay: '0.2s' }}></div>
                        <span>Generating sections...</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: 'var(--color-verified)', animationDelay: '0.4s' }}></div>
                        <span>Including your professional credentials...</span>
                      </div>
                    </div>

                    <p className="cs-meta mt-6">
                      Please wait... Do not close this window
                    </p>
                  </div>
                </div>
              )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Collaboration Tools Section */}
        <div style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }}>
          <h2 className="cs-h2 mb-4">Collaboration Tools</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Version History */}
            <VersionHistory
              reportId={reportId!}
              getAuthHeader={getAuthHeader}
            />
            
            {/* E-Signature */}
            <ESignature
              reportId={reportId!}
              getAuthHeader={getAuthHeader}
              canSign={permissions.includes('sign')}
            />
          </div>
        </div>
      </div>
      </div>
    </AccountantLayout>
  );
}
